import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { Button } from '@mui/material';

import { type MiddlewhereFilter } from './FilterItemManager';
import { type FiltersType } from '../hooks/useFilters';
import useSettings from '../hooks/useSettings';
import { alertLocalizedMsg } from '../Utils';
import ResponsiveDialog from '../dialogs/ResponsiveDialog';
import Form from '../Form';
import Filter from './Filter';
import FilterSaveDialog from './filter-saved/FilterSaveDialog';
import FilterLoadDialog from './filter-saved/FilterLoadDialog';

interface FilterDialogProps {
	/** True to show this dialog, false to hide it */
	open: boolean;
	/** Function used to close the dialog */
	closeDialog: () => void;
	/** The module name */
	module: string;
	/** An array of filters applied by default */
	defaultFilters: MiddlewhereFilter[];
	/** A list of current applied filters */
	currentFilters: FiltersType['currentFilters'];
	/** Function used to update the current filters */
	setCurrentFilters: FiltersType['setCurrentFilters'];
	/** Function used to update the filters */
	handleFilterChange: FiltersType['handleFilterChange'];
	/** Function used to set the filters */
	handleFiltersApply: FiltersType['handleFiltersApply'];
}

/** A dialog that show filters for a specified module */
export default function FilterDialog({
	open,
	closeDialog,
	module,
	defaultFilters,
	currentFilters,
	setCurrentFilters,
	handleFilterChange,
	handleFiltersApply
}: FilterDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [formId] = React.useState(uuidv4());
	const [isOpenSaveDialog, setIsOpenSaveDialog] = React.useState(false);
	const [isOpenLoadDialog, setIsOpenLoadDialog] = React.useState(false);

	function handleClear() {
		setCurrentFilters([]);
	}

	function handleApply() {
		handleFiltersApply();
		closeDialog();
	}

	function handleFilterSave() {
		if (currentFilters.length === 0) dispatch(alertLocalizedMsg(t, 'ERROR_FILTER_SAVE_EMPTY', 'warning'));
		else setIsOpenSaveDialog(true);
	}

	function handleFilterLoad() {
		setIsOpenLoadDialog(true);
	}

	return (
		<>
			{/* EDIT FILTER DIALOG */}
			<ResponsiveDialog
				open={open}
				onClose={closeDialog}
				maxWidth="lg"
				scroll="body"
				title={t('DIALOG_TITLE_FILTER')}
				content={
					<Form id={formId} onSubmit={handleApply}>
						<Filter
							module={module}
							defaultFilters={defaultFilters}
							currentFilters={currentFilters}
							handleFilterChange={handleFilterChange}
						/>
					</Form>
				}
				actions={
					<>
						<Button onClick={closeDialog}>{t('DIALOG_ACTION_BACK')}</Button>
						<Button onClick={handleFilterSave}>{t('DIALOG_ACTION_SAVE_FILTER')}</Button>
						<Button onClick={handleFilterLoad}>{t('DIALOG_ACTION_LOAD_FILTER')}</Button>
						<Button onClick={handleClear}>{t('DIALOG_ACTION_CLEAR')}</Button>
						<Button type="submit" form={formId} color="secondary">
							{t('DIALOG_ACTION_APPLY')}
						</Button>
					</>
				}
			/>
			{/* SAVE FILTER DIALOG */}
			<FilterSaveDialog
				module={module}
				isOpen={isOpenSaveDialog}
				closeDialog={() => setIsOpenSaveDialog(false)}
				currentFilters={currentFilters}
			/>
			{/* LOAD FILTER DIALOG */}
			<FilterLoadDialog
				module={module}
				isOpen={isOpenLoadDialog}
				closeDialog={() => setIsOpenLoadDialog(false)}
				setCurrentFilters={setCurrentFilters}
			/>
		</>
	);
}
